import { Popover } from "radix-ui";
import clsx from "clsx";
import { useMemo } from "react";

import {
  MOBILE_ACTION_BUTTON_BG,
  FONT_FAMILY,
  getFontFamilyString,
} from "@excalidraw/common";

import type { FontFamilyValues } from "@excalidraw/element/types";

import { t } from "../../i18n";
import {
  FontFamilyCodeIcon,
  FontFamilyHeadingIcon,
  FontFamilyNormalIcon,
  FreedrawIcon,
} from "../icons";

import { useExcalidrawSetAppState, useExplorerPropertiesPortal } from "../App";

import type { JSX } from "react";

const getFontFamilyIcon = (
  fontFamily: FontFamilyValues | null,
): JSX.Element => {
  switch (fontFamily) {
    case FONT_FAMILY.OnePaper:
    case FONT_FAMILY.Excalifont:
    case FONT_FAMILY.Virgil:
      return FreedrawIcon;
    case FONT_FAMILY.Nunito:
    case FONT_FAMILY.Helvetica:
      return FontFamilyNormalIcon;
    case FONT_FAMILY["Lilita One"]:
      return FontFamilyHeadingIcon;
    case FONT_FAMILY["Comic Shanns"]:
    case FONT_FAMILY.Cascadia:
    case FONT_FAMILY["Geist Mono"]:
      return FontFamilyCodeIcon;
    default:
      return FontFamilyNormalIcon;
  }
};

interface FontPickerTriggerProps {
  selectedFontFamily: FontFamilyValues | null;
  isOpened?: boolean;
  compactMode?: boolean;
}

export const FontPickerTrigger = ({
  selectedFontFamily,
  isOpened = false,
  compactMode = false,
}: FontPickerTriggerProps) => {
  const setAppState = useExcalidrawSetAppState();
  const explorerPropertiesPortal = useExplorerPropertiesPortal();

  const compactStyle = compactMode
    ? {
        ...MOBILE_ACTION_BUTTON_BG,
        width: "2rem",
        height: "2rem",
      }
    : {};

  const fontName = useMemo(() => {
    if (!selectedFontFamily) {
      return t("labels.normal");
    }
    const entries = Object.entries(FONT_FAMILY);
    for (const [key, value] of entries) {
      if (value === selectedFontFamily) {
        if (key === "Nunito") {
          return t("labels.normal");
        }
        if (key === "Comic Shanns") {
          return t("labels.code");
        }
        if (key === "OnePaper" || key === "Excalifont") {
          return t("labels.handDrawn");
        }
        return key;
      }
    }
    return t("labels.normal");
  }, [selectedFontFamily]);

  const fontStyle = useMemo(
    () => ({
      fontFamily: getFontFamilyString({
        fontFamily: selectedFontFamily || FONT_FAMILY.Nunito,
      }),
    }),
    [selectedFontFamily],
  );

  const icon = useMemo(
    () => getFontFamilyIcon(selectedFontFamily),
    [selectedFontFamily],
  );

  return (
    <Popover.Trigger asChild>
      <div
        data-openpopup="fontFamily"
        className="properties-trigger"
        style={{ width: compactMode ? "auto" : "100%" }}
      >
        <button
          type="button"
          title={t("labels.showFonts")}
          className={clsx("properties-trigger", { active: isOpened })}
          data-testid={"font-family-show-fonts"}
          onClick={() => {
            if (explorerPropertiesPortal) {
              explorerPropertiesPortal.setOpenPopup((openPopup) =>
                openPopup === "fontFamily" ? null : "fontFamily",
              );
              setAppState({ openPopup: null });
              return;
            }

            setAppState((appState) => ({
              openPopup:
                appState.openPopup === "fontFamily" ? null : appState.openPopup,
            }));
          }}
          style={{
            border: "1px solid var(--button-gray-2)",
            borderRadius: "0.5rem",
            padding: compactMode ? "0" : "0 0.5rem",
            height: compactMode ? "auto" : "var(--default-button-size, 2rem)",
            background: "var(--button-bg, transparent)",
            color: "var(--icon-fill-color)",
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            ...compactStyle,
          }}
        >
          <div
            className="dropdown-menu-item__icon"
            style={{
              display: "flex",
              width: "1.25rem",
              height: "1.25rem",
              flexShrink: 0,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {icon}
          </div>
          {!compactMode && (
            <span
              style={{
                ...fontStyle,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {fontName}
            </span>
          )}
        </button>
      </div>
    </Popover.Trigger>
  );
};
